import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { Box, Button, Separator, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { MobileDeviceEnhancer } from '~/components/MobileDeviceEnhancer/MobileDeviceEnhancer';
import { routes } from '~/constants';
import { useStore } from '~/contexts';
import { useClientView } from '~/hooks/useClientView';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useSwitchBusiness } from '~/hooks/useSwitchBusiness';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { BusinessSwitcherForSpecificUsers } from '../BusinessSwitcher/BusinessSwitcher';
import { HelpMenu } from '../HelpMenu';
import { InAppNotificationsButton } from '../InAppNotificationsButton';
import { TasksTriggerButton } from '../TasksTriggerButton/TasksTriggerButton';
import UserMenu from '../UserMenu/UserMenu';
import { QuickActionsMenu } from './QuickActionsMenu/QuickActionsMenu';
import { SidebarMenu } from './SidebarMenu/SidebarMenu';
import styles from './TopBar.module.scss';
const AnchorAdmin = (param)=>{
    let { text, testId } = param;
    return /*#__PURE__*/ _jsx(Typography, {
        variant: "paragraph2",
        breakWords: true,
        testId: testId,
        children: text
    });
};
export const getTopBarTID = ()=>getTestIds();
export function TopBar() {
    const { TopBarTID } = useMemo(()=>getTopBarTID(), []);
    const isMobileDevice = useIsMobileForHomePage();
    const userInfo = useStore((state)=>state.userInfo);
    const userId = useStore(selectors.user.id);
    const isVendor = useStore(selectors.user.isVendor);
    const businessName = useStore(selectors.user.businessName);
    const isAccountant = useStore(selectors.user.isAccountant);
    const isAnchorAdmin = useStore(selectors.user.isAnchorAdmin);
    const isViewMode = useStore(selectors.isViewMode);
    const { switchBusiness, getPrevBusinessId } = useSwitchBusiness();
    const { exitClientView } = useClientView();
    const { t: topBarTexts } = useLocaleKeys('components.navigation.topBar');
    const showBannerStrip = isMobileDevice && (isAccountant || isViewMode);
    const accountantBanner = isAccountant && /*#__PURE__*/ _jsxs(Box, {
        className: cn(styles.accountantSection, {
            [styles.inStrip]: showBannerStrip
        }),
        testId: TopBarTID.AccountantBanner(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                children: topBarTexts.accountantBanner({
                    businessName
                })
            }),
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'switch_back_to_previous_business'
                },
                className: styles.backBtn,
                size: "small",
                skin: "primaryOutline",
                testId: TopBarTID.BackToMyBusiness(),
                onClick: ()=>switchBusiness(getPrevBusinessId(), {
                        newRoute: routes.agreementsDashboard
                    }),
                children: topBarTexts.backToMyBusiness()
            })
        ]
    });
    const viewModeBanner = isViewMode && /*#__PURE__*/ _jsxs(Box, {
        className: cn(styles.viewModeSection, {
            [styles.inStrip]: showBannerStrip
        }),
        testId: TopBarTID.ViewModeBanner(),
        children: [
            /*#__PURE__*/ _jsxs(Typography, {
                variant: "paragraph2",
                children: [
                    topBarTexts.viewModeBanner(),
                    ' ',
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        weight: "bold",
                        testId: TopBarTID.ViewModeBannerBusinessName(),
                        children: businessName
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'exit_view_mode'
                },
                className: styles.backBtn,
                size: "small",
                skin: "primaryOutline",
                testId: TopBarTID.ViewModeBackToMyBusiness(),
                onClick: exitClientView,
                children: topBarTexts.backToMyBusiness()
            })
        ]
    });
    const banners = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            accountantBanner,
            viewModeBanner
        ]
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": TopBarTID.Root(),
                className: cn(styles.root, {
                    [styles.accountant]: isAccountant,
                    [styles.anchorAdmin]: isAnchorAdmin,
                    [styles.viewMode]: isViewMode,
                    [styles.mobile]: isMobileDevice
                }),
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        className: styles.start,
                        testId: TopBarTID.Start(),
                        children: [
                            isMobileDevice && /*#__PURE__*/ _jsx(SidebarMenu, {}),
                            !isMobileDevice && banners,
                            isAnchorAdmin && businessName && /*#__PURE__*/ _jsx(AnchorAdmin, {
                                text: topBarTexts.anchorAdminBanner({
                                    companyName: businessName
                                }),
                                testId: TopBarTID.AnchorAdminBanner()
                            }),
                            !isMobileDevice && /*#__PURE__*/ _jsx(BusinessSwitcherForSpecificUsers, {})
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: cn(styles.end, {
                            [styles.mobile]: isMobileDevice
                        }),
                        "data-testid": TopBarTID.EndContainer(isVendor ? 'vendor' : 'client'),
                        children: [
                            compact([
                                {
                                    id: 'marketing-content',
                                    value: null
                                },
                                {
                                    id: 'help',
                                    value: userId && /*#__PURE__*/ _jsx(HelpMenu, {})
                                },
                                userId && {
                                    id: 'tasks',
                                    value: /*#__PURE__*/ _jsx(TasksTriggerButton, {})
                                },
                                isVendor && {
                                    id: 'in-app-notifications',
                                    value: /*#__PURE__*/ _jsx(InAppNotificationsButton, {})
                                },
                                userInfo && userId && {
                                    id: 'user',
                                    value: /*#__PURE__*/ _jsx(UserMenu, {
                                        userInfo: userInfo
                                    })
                                }
                            ]).map((param)=>{
                                let { id, value } = param;
                                return /*#__PURE__*/ _jsx("div", {
                                    "data-testid": TopBarTID.EndItem(id),
                                    className: styles.endItem,
                                    children: value
                                }, id);
                            }),
                            isMobileDevice && isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Separator, {
                                        vertical: true,
                                        spacing: "none",
                                        height: 32
                                    }),
                                    /*#__PURE__*/ _jsx(QuickActionsMenu, {})
                                ]
                            })
                        ]
                    })
                ]
            }),
            showBannerStrip && /*#__PURE__*/ _jsx(MobileDeviceEnhancer, {
                maxHeight: false,
                children: /*#__PURE__*/ _jsx(Box, {
                    className: styles.bannerStrip,
                    testId: TopBarTID.BannerStrip(),
                    children: banners
                })
            })
        ]
    });
}
